import _ from 'lodash';
import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { TextField } from '@material-ui/core';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import NgpField from './NgpField';
import NgpFieldHelpTip from './NgpFieldHelpTip';
import { useStyles } from './FormStyles';
import { GENERIC_REQUIRED_MESSAGE } from '../../utilities/FormUtilities';

const validNumberText = i18n._(t`Value must be numeric`);

// A sentinel value stored in form state when the browser reports badInput.
// This lets the validate rule detect an invalid number even though the browser
// reports e.target.value as "".
export const BAD_INPUT_SENTINEL = '__BAD_INPUT__';

const validIntegerText = i18n._(t`Value must be a whole number`);

function buildRules({ required, min, max, integerOnly }) {
    const rules = {};

    if (required) {
        rules.required = GENERIC_REQUIRED_MESSAGE;
    }

    rules.validate = (value) => {
        if (value === BAD_INPUT_SENTINEL) {
            return validNumberText;
        }

        if (!value && value !== 0) {
            return true;
        }

        const num = parseFloat(value);
        if (_.isNaN(num)) {
            return validNumberText;
        }

        if (integerOnly && !Number.isInteger(num)) {
            return validIntegerText;
        }

        if (!_.isNil(min) && num < parseFloat(min)) {
            return i18n._(t`Value must be greater than or equal to ${min}`);
        }

        if (!_.isNil(max) && num > parseFloat(max)) {
            return i18n._(t`Value must be less than or equal to ${max}`);
        }

        return true;
    };

    return rules;
}

/**
 * A number field using native <input type="number"> via MUI TextField.
 * Provides browser-native spinner arrows and keyboard arrow-key stepping.
 *
 * @param {object} props
 * @param {string} props.fieldName - react-hook-form field name
 * @param {string} [props.label] - field label
 * @param {string} [props.helpTipText] - help tip text
 * @param {boolean} [props.required] - whether the field is required
 * @param {number|string} [props.min] - minimum allowed value
 * @param {number|string} [props.max] - maximum allowed value
 * @param {number|string} [props.step] - step increment (default 1; use "any" for decimals)
 * @param {boolean} [props.disabled] - disable the input
 * @param {boolean} [props.fullWidth] - MUI fullWidth
 * @param {number|string} [props.inputWidth] - width applied to the MUI InputBase root
 * @param {string} [props.className] - additional className
 */
export default function NgpNativeNumberField({
    fieldName,
    label,
    helpTipText,
    required = false,
    min,
    max,
    step = 1,
    disabled = false,
    fullWidth = false,
    inputWidth,
    className,
    hideError = false,
    inputProps,
    InputProps: customInputProps,
    ...rest
}) {
    const classes = useStyles();
    const { control, errors } = useFormContext();
    const integerOnly = step !== 'any';
    const rules = buildRules({ required, min, max, integerOnly });
    const mergedInputProps = {
        ...inputProps,
        min,
        max,
        step,
    };
    const mergedMuiInputProps = inputWidth
        ? {
              ...customInputProps,
              style: {
                  ...(customInputProps?.style || {}),
                  width: inputWidth,
              },
          }
        : customInputProps;
    const textFieldProps = {
        ...rest,
        type: 'number',
        label,
        required,
        disabled,
        fullWidth,
        className,
        inputProps: mergedInputProps,
        size: 'small',
    };

    if (mergedMuiInputProps) {
        textFieldProps.InputProps = mergedMuiInputProps;
    }

    const handleChange = useCallback(
        (onChange) => (e) => {
            if (e.target.validity && e.target.validity.badInput) {
                onChange(BAD_INPUT_SENTINEL);
            } else {
                onChange(e.target.value);
            }
        },
        []
    );

    const field = (
        <Controller
            control={control}
            name={fieldName}
            defaultValue=""
            rules={rules}
            render={({ value, onChange, onBlur }) => (
                <TextField
                    {...textFieldProps}
                    value={value === BAD_INPUT_SENTINEL ? '' : value ?? ''}
                    onChange={handleChange(onChange)}
                    onBlur={onBlur}
                    error={!_.isEmpty(_.get(errors, fieldName))}
                />
            )}
        />
    );

    if (hideError) {
        return (
            <div className={classes.ngpFormFieldContainer}>
                {field}
                {helpTipText && <NgpFieldHelpTip helpText={helpTipText} />}
            </div>
        );
    }

    return (
        <NgpField fieldName={fieldName}>
            {field}
            {helpTipText && <NgpFieldHelpTip helpText={helpTipText} />}
        </NgpField>
    );
}
